import React, { FC } from "react"

import {
    Alert,
    Button,
    Collapse,
    CopyButton,
    Group,
    Modal,
    ModalProps,
    MultiSelect,
    Stack,
    Switch,
    Text,
    TextInput,
    useMantineTheme,
} from "@mantine/core"
import { IconAt, IconCheck, IconCopy, IconKey, IconPhone, IconPlus } from "@tabler/icons-react"

import { useLanguage } from "../../../contexts/language-context"
import useCreateUser from "./useCreateUser"
import { useGroups } from "../../../contexts/devices-context"

type CreateUserModalProps = ModalProps

const CreateUserModal: FC<CreateUserModalProps> = props => {
    const { form, onSubmit, processing, password } = useCreateUser()
    const { L } = useLanguage()
    const { data: groups } = useGroups()
    const theme = useMantineTheme()

    return (
        <Modal {...props} title={<Text weight="bold">{L("Create new user")}</Text>}>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Stack>
                    <TextInput label={L("Name")} placeholder={L("Name")} {...form.getInputProps("name")} />
                    <TextInput
                        label={L("Email address")}
                        placeholder={L("Email address")}
                        icon={<IconAt size={16} />}
                        {...form.getInputProps("email")}
                    />
                    <TextInput
                        label={L("Phone number")}
                        description={L("Optional")}
                        placeholder={L("Phone number")}
                        icon={<IconPhone size={16} />}
                        {...form.getInputProps("phone")}
                    />
                    <Text mt="xl" weight="bold">
                        {L("Group access")}
                    </Text>
                    <Switch
                        label={L("Grant full access")}
                        description={L("Grant the new user full access to all groups.")}
                        checked={form.values.fullAccess}
                        {...form.getInputProps("fullAccess")}
                    />
                    <Collapse in={!form.values.fullAccess}>
                        <MultiSelect
                            data={groups.map(({ id }) => id)}
                            label={L("Groups")}
                            description={L("Select the groups to which the user has access.")}
                            placeholder={L("Groups")}
                            {...form.getInputProps("groups")}
                        />
                    </Collapse>
                    {password && (
                        <>
                            <Text mt="xl" weight="bold">
                                {L("Next steps")}
                            </Text>
                            <Alert title={"New password"} icon={<IconKey size={18} />}>
                                <Stack>
                                    {L("We have created a new password for %s:", form.values.name)}
                                    <CopyButton value={password} timeout={2000}>
                                        {({ copied, copy }) => (
                                            <TextInput
                                                value={password}
                                                icon={
                                                    copied ? (
                                                        <IconCheck stroke={2} size={18} color={theme.colors.green[7]} />
                                                    ) : (
                                                        <IconCopy size={18} />
                                                    )
                                                }
                                                rightSection={
                                                    <Text size="xs" color="dimmed">
                                                        {copied ? L("Copied!") : L("Click to copy")}
                                                    </Text>
                                                }
                                                onClick={copy}
                                                styles={{
                                                    input: {
                                                        "&, &:focus, :focus-within": {
                                                            cursor: "pointer",
                                                            borderColor:
                                                                theme.colorScheme === "dark"
                                                                    ? copied
                                                                        ? theme.colors.green[8]
                                                                        : theme.colors.gray[8]
                                                                    : copied
                                                                    ? theme.colors.green[7]
                                                                    : theme.colors.gray[4],
                                                        },
                                                    },
                                                    rightSection: {
                                                        width: "unset",
                                                        marginRight: "1rem",
                                                    },
                                                }}
                                                readOnly
                                            />
                                        )}
                                    </CopyButton>
                                    {L(
                                        'Please share the password with them in a secure manner and request them to reset their password immediately after. This can be done through the "Forgot password" button when signing in.',
                                    )}
                                </Stack>
                            </Alert>
                        </>
                    )}
                    {!password && (
                        <Group position="right" noWrap>
                            <Button variant="subtle" onClick={props.onClose}>
                                {L("Cancel")}
                            </Button>
                            <Button type="submit" variant="filled" leftIcon={<IconPlus size={16} />} loading={processing}>
                                {L("Create user")}
                            </Button>
                        </Group>
                    )}
                </Stack>
            </form>
        </Modal>
    )
}

export default CreateUserModal
